import {
  HOME_PREVIEW_KEYS,
  HOME_PREVIEWS,
  type HomePreviewKey,
  svgHome,
} from '@runtime/core/theme/assets'
import {
  buildGeneratedThemePalette,
  buildGeneratedThemePuzzlePosterTiles,
  buildGeneratedThemeTiles,
} from '@runtime/core/theme/generatedThemeAssets'
import { useAppStore } from '@runtime/core/store/appStore'
import { requestPosterThemeAccess } from '@runtime/core/theme/posterThemeAccessApi'
import { registerDynamicPosterAccess } from '@runtime/core/theme/posterAccess'
import {
  getPosterThemeAccessToken,
  setPosterThemeAccessToken,
} from '@runtime/core/theme/posterAccess'
import type {
  PosterThemeConfig,
  ThemeTiles,
} from '@runtime/core/theme/themeEngineShared'
import {
  normalizeThemeQueryFallbackId,
  normalizeThemeQueryId,
  normalizeThemeQueryLookup,
} from '@runtime/core/theme/themeQuery'
import {
  fetchJSON as fetchApiJSON,
  resolveApiUrl,
} from '@runtime/shared/api/fetchJSON'
import type { ThemeIndexItem } from '@runtime/features/poster/logic/posterTheme.types'

type ThemeIndexEntry = {
  id?: string
  name?: string
  preview?: boolean | string | null
  protected?: boolean
  password?: string | null
  allowedEmails?: string[]
  allowedPseudos?: string[]
}

type MutablePosterThemeConfig = PosterThemeConfig & {
  tiles?: ThemeTiles
  homePreviews?: Partial<Record<HomePreviewKey, string>> &
    Record<string, string>
  puzzlePosterTiles?: string[]
}

export type ResolvedPosterThemeConfig = MutablePosterThemeConfig

type ThemeRuntimeAssets = {
  preview?: string
  homePreviews: Partial<Record<HomePreviewKey, string>> & Record<string, string>
  puzzlePosterBase?: string
  puzzlePosterTiles?: string[]
  puzzlePreviews?: {
    united?: string
    poster?: string
    switch?: string
  }
}

const THEME_RUNTIME_ASSETS = new Map<string, ThemeRuntimeAssets>()
export const DEFAULT_POSTER_THEME_ID = 'united-square-original'
const EMBEDDED_DEFAULT_THEME_ID = DEFAULT_POSTER_THEME_ID
const EMBEDDED_DEFAULT_THEME_COLORS = {
  top: '#FFCE28',
  right: '#E4332F',
  bottom: '#067DBF',
  left: '#7AB543',
} as const
const EMBEDDED_DEFAULT_THEME_TILES = buildGeneratedThemeTiles(
  EMBEDDED_DEFAULT_THEME_COLORS,
)
const EMBEDDED_DEFAULT_THEME_HOME_PREVIEWS = {
  strategy: svgHome('UNITED', '#E4332F'),
  puzzle: svgHome('PUZZLE', '#067DBF'),
  drop: svgHome('DROP', '#FFCE28'),
  online: svgHome('ONLINE', '#7AB543'),
  ranking: svgHome('RANKING', '#FFCE28'),
  challenges: svgHome('CHALLENGES', '#E4332F'),
  tools: svgHome('TOOLS', '#067DBF'),
  arcade: svgHome('ARCADE', '#7AB543'),
} as const
const EMBEDDED_DEFAULT_THEME_PUZZLE_PREVIEWS = {
  united: svgHome('UNITED', '#E4332F'),
  poster: svgHome('POSTER', '#067DBF'),
  switch: svgHome('SWITCH', '#7AB543'),
} as const
const EMBEDDED_DEFAULT_THEME_PUZZLE_TILES = Array.from(
  { length: 25 },
  (_, index) =>
    buildPuzzlePosterTileUrl(
      buildThemePuzzlePosterBaseUrl(EMBEDDED_DEFAULT_THEME_ID),
      `POSTER_CARRE_${String(index + 1).padStart(2, '0')}.png`,
    ),
)

function buildGeneratedThemeHomePreviews(
  playerColors: readonly string[] | undefined,
): Partial<Record<HomePreviewKey, string>> & Record<string, string> {
  const colors = {
    strategy: playerColors?.[0] || '#E4332F',
    puzzle: playerColors?.[1] || '#067DBF',
    online: playerColors?.[3] || '#7AB543',
    ranking: playerColors?.[2] || '#FFCE28',
    challenges: playerColors?.[0] || '#E4332F',
    tools: playerColors?.[1] || '#067DBF',
    arcade: playerColors?.[3] || '#7AB543',
    drop: playerColors?.[2] || '#FFCE28',
  }

  return {
    strategy: svgHome('UNITED', colors.strategy),
    puzzle: svgHome('PUZZLE', colors.puzzle),
    online: svgHome('ONLINE', colors.online),
    ranking: svgHome('RANKING', colors.ranking),
    challenges: svgHome('CHALLENGES', colors.challenges),
    tools: svgHome('TOOLS', colors.tools),
    arcade: svgHome('ARCADE', colors.arcade),
    drop: svgHome('DROP', colors.drop),
  }
}

function buildEmbeddedDefaultTheme(): MutablePosterThemeConfig {
  return {
    id: EMBEDDED_DEFAULT_THEME_ID,
    name: 'UNITED SQUARE ORIGINAL',
    preview: svgPoster('United Square'),
    palette: {
      players: ['#E4332F', '#067DBF', '#FFCE28', '#7AB543'],
      primary: '#E4332F',
      secondary: '#067DBF',
      surface: '#191D24',
      surfaceVariant: '#272C36',
      text: '#E9EDF3',
      border: '#3B4356',
    },
    tiles: {
      center: EMBEDDED_DEFAULT_THEME_TILES.center,
      set: [...(EMBEDDED_DEFAULT_THEME_TILES.set || [])],
    },
    homePreviews: { ...EMBEDDED_DEFAULT_THEME_HOME_PREVIEWS },
    puzzlePreviews: { ...EMBEDDED_DEFAULT_THEME_PUZZLE_PREVIEWS },
    puzzlePosterTiles: [...EMBEDDED_DEFAULT_THEME_PUZZLE_TILES],
  }
}

function appendThemeAccessQuery(
  url: string,
  accessToken?: string | null,
): string {
  if (!accessToken) return url
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}access=${encodeURIComponent(accessToken)}`
}

function resolveThemeAccessToken(
  themeId: string,
  accessToken?: string | null,
): string | null {
  const nextAccessToken = accessToken || getPosterThemeAccessToken(themeId)
  return nextAccessToken ? String(nextAccessToken) : null
}

export function buildThemeConfigUrl(
  themeId: string,
  accessToken?: string | null,
): string {
  return resolveApiUrl(
    appendThemeAccessQuery(
      `/api/poster-themes/${encodeURIComponent(themeId)}/config`,
      resolveThemeAccessToken(themeId, accessToken),
    ),
  )
}

export function buildThemePreviewAssetUrl(
  themeId: string,
  fileName: string,
  accessToken?: string | null,
): string {
  return resolveApiUrl(
    appendThemeAccessQuery(
      `/api/poster-themes/${encodeURIComponent(themeId)}/preview/${encodeURIComponent(fileName)}`,
      resolveThemeAccessToken(themeId, accessToken),
    ),
  )
}

export function buildThemeTileAssetUrl(
  themeId: string,
  fileName: string,
  accessToken?: string | null,
): string {
  return resolveApiUrl(
    appendThemeAccessQuery(
      `/api/poster-themes/${encodeURIComponent(themeId)}/tiles/${encodeURIComponent(fileName)}`,
      resolveThemeAccessToken(themeId, accessToken),
    ),
  )
}

export function buildThemePuzzlePosterBaseUrl(
  themeId: string,
  accessToken?: string | null,
): string {
  return resolveApiUrl(
    appendThemeAccessQuery(
      `/api/poster-themes/${encodeURIComponent(themeId)}/puzzle-poster/`,
      resolveThemeAccessToken(themeId, accessToken),
    ),
  )
}

function getThemeHomePreviewFallback(
  themeId: string,
  key: HomePreviewKey | 'arcade' | 'tools',
): string {
  return buildThemePreviewAssetUrl(
    themeId,
    key === 'strategy' ? 'preview-home-united.png' : `preview-home-${key}.png`,
  )
}

function normalizeLiveThemeApiUrl(value: string): string {
  return value.startsWith('/api/') ? resolveApiUrl(value) : value
}

function normalizeResolvedThemeAssetValue(
  value: string | null | undefined,
  fallback?: string,
): string | undefined {
  if (!value) return fallback
  if (/^(https?:|data:|blob:)/i.test(value)) return value
  if (value.startsWith('/')) return normalizeLiveThemeApiUrl(value)
  if (/^assets\//i.test(value)) return `/${value}`
  return fallback
}

function normalizeThemePreviewAssetValue(
  themeId: string,
  value: string | null | undefined,
  fallbackFileName?: string,
): string | undefined {
  const raw = String(value || '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/^preview\//, '')
  const normalized = normalizeResolvedThemeAssetValue(raw)
  if (normalized) return normalized
  if (raw) return buildThemePreviewAssetUrl(themeId, raw)
  return fallbackFileName
    ? buildThemePreviewAssetUrl(themeId, fallbackFileName)
    : undefined
}

function normalizeThemeTileAssetValue(
  themeId: string,
  value: string | null | undefined,
  fallbackFileName?: string,
): string | undefined {
  const raw = String(value || '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/^tiles\//, '')
  const normalized = normalizeResolvedThemeAssetValue(raw)
  if (normalized) return normalized
  if (raw) return buildThemeTileAssetUrl(themeId, raw)
  return fallbackFileName
    ? buildThemeTileAssetUrl(themeId, fallbackFileName)
    : undefined
}

function normalizeThemeHomePreviews(
  themeId: string,
  homePreviews?: Partial<Record<HomePreviewKey, string>> &
    Record<string, string>,
):
  | (Partial<Record<HomePreviewKey, string>> & Record<string, string>)
  | undefined {
  if (!homePreviews || typeof homePreviews !== 'object') return undefined

  const nextHomePreviews: Partial<Record<HomePreviewKey, string>> &
    Record<string, string> = {}

  Object.entries(homePreviews).forEach(([key, value]) => {
    if (!value) return
    const fallback = HOME_PREVIEW_KEYS.includes(key as HomePreviewKey)
      ? getThemeHomePreviewFallback(themeId, key as HomePreviewKey)
      : undefined
    const normalized =
      normalizeThemePreviewAssetValue(themeId, value, undefined) || fallback
    if (normalized) {
      nextHomePreviews[key] = normalized
    }
  })

  return Object.keys(nextHomePreviews).length ? nextHomePreviews : undefined
}

function normalizeThemeGeneratorAssetValue(
  themeId: string,
  value: string | null | undefined,
): string | undefined {
  const raw = String(value || '')
    .trim()
    .replace(/\\/g, '/')
  return normalizeResolvedThemeAssetValue(
    raw,
    raw
      ? buildThemePreviewAssetUrl(themeId, raw.replace(/^preview\//, ''))
      : undefined,
  )
}

function normalizeThemePuzzlePosterBaseAssetValue(
  themeId: string,
  value: string | null | undefined,
): string | undefined {
  const raw = String(value || '')
    .trim()
    .replace(/\\/g, '/')
  if (!raw) return undefined

  const normalized = normalizeResolvedThemeAssetValue(raw)
  if (normalized) return normalized

  const path = raw.replace(/^\/+/, '').split('?', 1)[0]
  if (path === 'puzzle-poster' || path === 'puzzle-poster/') {
    return buildThemePuzzlePosterBaseUrl(themeId)
  }

  return raw
}

function buildPuzzlePosterTileUrl(base: string, fileName: string): string {
  const [pathPart, queryPart] = String(base || '').split('?', 2)
  const normalizedBase = pathPart.endsWith('/') ? pathPart : `${pathPart}/`
  return queryPart
    ? `${normalizedBase}${fileName}?${queryPart}`
    : `${normalizedBase}${fileName}`
}

/* Utilities */
export function svgPoster(label: string): string {
  const s = 64
  const esc = (str: unknown) =>
    String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
  const svg =
    "<svg xmlns='http://www.w3.org/2000/svg' width='" +
    s +
    "' height='" +
    s +
    "' viewBox='0 0 " +
    s +
    ' ' +
    s +
    "'><rect width='100%' height='100%' rx='10' ry='10' fill='#273046'/><text x='32' y='38' text-anchor='middle' font-family='Inter,Arial' font-weight='800' font-size='10' fill='#d9e0ee'>" +
    esc(label) +
    '</text></svg>'
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
}

function toThemeAssetUrl(value: unknown): string | null {
  if (!value) return null
  const raw = String(value)
  if (/^(data:|blob:)/i.test(raw)) return null
  if (/^https?:/i.test(raw)) {
    // Resolved configs use absolute API URLs in production. Cache only our theme API.
    try {
      const api = new URL(
        resolveApiUrl('/api/poster-themes/'),
        window.location.origin,
      )
      const asset = new URL(raw)
      return asset.origin === api.origin &&
        asset.pathname.startsWith(api.pathname)
        ? asset.href
        : null
    } catch {
      return null
    }
  }
  if (raw.startsWith('/')) return normalizeLiveThemeApiUrl(raw)
  if (/^assets\//i.test(raw)) return '/' + raw
  return null
}

function buildThemeAssetUrls(
  themeId: string,
  theme?: MutablePosterThemeConfig | null,
): string[] {
  const urls = new Set<string>()
  urls.add(buildThemeConfigUrl(themeId))

  const accessToken = getPosterThemeAccessToken(themeId)
  if (accessToken) {
    urls.add(buildThemeConfigUrl(themeId, accessToken))
  }

  if (theme) {
    const preview = toThemeAssetUrl(theme.preview)
    if (preview) urls.add(preview)

    if (theme.tiles) {
      const center = toThemeAssetUrl(theme.tiles.center)
      if (center) urls.add(center)
      if (Array.isArray(theme.tiles.set)) {
        theme.tiles.set.forEach((tile) => {
          const url = toThemeAssetUrl(tile)
          if (url) urls.add(url)
        })
      }
    }

    if (theme.homePreviews && typeof theme.homePreviews === 'object') {
      Object.values(theme.homePreviews).forEach((value) => {
        const url = toThemeAssetUrl(value)
        if (url) urls.add(url)
      })
    }

    if (theme.puzzlePreviews && typeof theme.puzzlePreviews === 'object') {
      Object.values(theme.puzzlePreviews).forEach((value) => {
        const url = toThemeAssetUrl(value)
        if (url) urls.add(url)
      })
    }
  }

  const puzzlePosterTiles =
    theme?.puzzlePosterTiles ||
    THEME_RUNTIME_ASSETS.get(themeId)?.puzzlePosterTiles
  if (puzzlePosterTiles?.length) {
    puzzlePosterTiles.forEach((tile) => {
      const url = toThemeAssetUrl(tile)
      if (url) urls.add(url)
    })
  }

  return Array.from(urls)
}

function rememberThemeRuntimeAssets(
  themeId: string,
  theme?: MutablePosterThemeConfig | null,
): void {
  if (!theme) return

  THEME_RUNTIME_ASSETS.set(themeId, {
    preview: theme.preview,
    homePreviews: { ...(theme.homePreviews || {}) },
    ...(theme.puzzlePosterBase
      ? { puzzlePosterBase: theme.puzzlePosterBase }
      : {}),
    ...(theme.puzzlePosterTiles?.length
      ? { puzzlePosterTiles: [...theme.puzzlePosterTiles] }
      : {}),
    ...(theme.puzzlePreviews
      ? { puzzlePreviews: { ...theme.puzzlePreviews } }
      : {}),
  })
}

export function getThemePosterPreview(themeId: string): string | null {
  return THEME_RUNTIME_ASSETS.get(themeId)?.preview || null
}

export function getThemeHomePreview(
  themeId: string,
  key: HomePreviewKey | 'arcade' | 'tools',
): string {
  const runtime = THEME_RUNTIME_ASSETS.get(themeId)
  const runtimeValue = runtime?.homePreviews?.[key]
  if (runtimeValue) return runtimeValue
  if (runtime) {
    const embeddedValue = EMBEDDED_DEFAULT_THEME_HOME_PREVIEWS[key]
    if (embeddedValue) return embeddedValue
  }
  if (themeId === EMBEDDED_DEFAULT_THEME_ID) {
    const embeddedValue = EMBEDDED_DEFAULT_THEME_HOME_PREVIEWS[key]
    if (embeddedValue) return embeddedValue
  }
  return getThemeHomePreviewFallback(themeId, key)
}

export function hasThemeHomePreview(
  themeId: string,
  key: HomePreviewKey | 'arcade' | 'tools',
) {
  return !!THEME_RUNTIME_ASSETS.get(themeId)?.homePreviews?.[key]
}

export function getThemePuzzlePosterTileUrls(themeId: string): string[] {
  const runtime = THEME_RUNTIME_ASSETS.get(themeId)
  if (runtime?.puzzlePosterTiles?.length) {
    return [...runtime.puzzlePosterTiles]
  }
  if (themeId === EMBEDDED_DEFAULT_THEME_ID) {
    return [...EMBEDDED_DEFAULT_THEME_PUZZLE_TILES]
  }
  if (runtime && !runtime.puzzlePosterBase) {
    return [...EMBEDDED_DEFAULT_THEME_PUZZLE_TILES]
  }
  const base =
    runtime?.puzzlePosterBase || buildThemePuzzlePosterBaseUrl(themeId)
  return Array.from({ length: 25 }, (_, index) => {
    const fileName = `POSTER_CARRE_${String(index + 1).padStart(2, '0')}.png`
    return buildPuzzlePosterTileUrl(base, fileName)
  })
}

export function getThemePuzzlePreviews(themeId: string) {
  const runtime = THEME_RUNTIME_ASSETS.get(themeId)
  const embeddedFallback =
    runtime || themeId === EMBEDDED_DEFAULT_THEME_ID
      ? EMBEDDED_DEFAULT_THEME_PUZZLE_PREVIEWS
      : null
  return {
    united:
      runtime?.puzzlePreviews?.united ||
      embeddedFallback?.united ||
      buildThemePreviewAssetUrl(themeId, 'preview-puzzle-united.png'),
    poster:
      runtime?.puzzlePreviews?.poster ||
      embeddedFallback?.poster ||
      buildThemePreviewAssetUrl(themeId, 'preview-puzzle-poster.png'),
    switch:
      runtime?.puzzlePreviews?.switch ||
      embeddedFallback?.switch ||
      buildThemePreviewAssetUrl(themeId, 'preview-puzzle-poster-switch.png'),
  }
}

export async function precacheThemeAssets(
  themeId: string,
  theme?: MutablePosterThemeConfig | null,
): Promise<void> {
  if (typeof caches === 'undefined') return
  const urls = buildThemeAssetUrls(themeId, theme)
  if (!urls.length) return

  const cache = await caches.open('us-themes')
  await Promise.all(
    urls.map(async (url) => {
      try {
        const cached =
          (await cache.match(url)) ||
          (url.startsWith('/') ? await cache.match(url.slice(1)) : undefined)
        // Legacy opaque images cannot be read by the canvas consumers.
        if (cached?.ok && cached.type !== 'opaque') return
        const res = await fetch(url, { cache: 'no-store' })
        if (!res || !res.ok) return
        await cache.put(url, res)
      } catch {
        // ignore
      }
    }),
  )
}

function getFetchErrorStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object') return null
  const status = (error as { status?: unknown }).status
  return typeof status === 'number' ? status : null
}

async function fetchLiveThemeConfig(
  themeId: string,
  accessToken?: string | null,
): Promise<MutablePosterThemeConfig | null> {
  return fetchApiJSON<MutablePosterThemeConfig>(
    buildThemeConfigUrl(themeId, accessToken),
  )
}

/**
 * Returns the API-managed list of themes with {id, name, preview}.
 */
export async function loadThemesIndex(
  options: { strict?: boolean } = {},
): Promise<ThemeIndexItem[]> {
  const dynamicEntries =
    (await fetchApiJSON<ThemeIndexEntry[]>('/api/poster-themes/index').catch(
      (error) => {
        if (options.strict) throw error
        return []
      },
    )) || []

  registerDynamicPosterAccess(
    dynamicEntries.map((entry) => ({
      id: entry.id,
      protected: entry.protected,
    })),
  )

  const merged = new Map<string, ThemeIndexItem>()

  dynamicEntries.forEach((entry) => {
    const id = String(entry?.id || '').trim()
    if (!id) return

    const name = String(entry?.name || id)
    const accessToken = getPosterThemeAccessToken(id)
    const previewValue =
      typeof entry?.preview === 'string' && entry.preview.trim()
        ? normalizeLiveThemeApiUrl(entry.preview)
        : entry?.protected && accessToken
          ? buildThemePreviewAssetUrl(id, 'preview.png', accessToken)
          : entry?.preview
            ? buildThemePreviewAssetUrl(id, 'preview.png')
            : svgPoster(name)

    merged.set(id, {
      id,
      name,
      preview: previewValue,
      ...(entry?.protected ? { protected: true } : {}),
    })
  })

  return Array.from(merged.values())
}

export async function resolveThemeIdFromQueryValue(
  value: string,
): Promise<string | null> {
  const normalizedId = normalizeThemeQueryId(value)
  if (!normalizedId) return null

  const themes = await loadThemesIndex()
  const fallbackId = normalizeThemeQueryFallbackId(value)

  if (!themes.length) return fallbackId || normalizedId

  const directMatch = themes.find(
    (item) => normalizeThemeQueryId(item.id) === normalizedId,
  )
  if (directMatch) return directMatch.id

  const normalizedName = normalizeThemeQueryLookup(normalizedId)
  const nameMatch = themes.find(
    (item) => normalizeThemeQueryLookup(item.name) === normalizedName,
  )
  if (nameMatch) return nameMatch.id

  const fallbackIdMatch = themes.find(
    (item) => normalizeThemeQueryFallbackId(item.id) === fallbackId,
  )
  if (fallbackIdMatch) return fallbackIdMatch.id

  const fallbackNameMatch = themes.find(
    (item) => normalizeThemeQueryFallbackId(item.name) === fallbackId,
  )
  if (fallbackNameMatch) return fallbackNameMatch.id

  return fallbackId || normalizedId
}

/**
 * Resolve a full theme config (palette, tiles, previews) from the poster themes API.
 * Only the built-in default theme remains embedded as a last-resort fallback.
 */
export async function resolveTheme(
  id: string,
): Promise<MutablePosterThemeConfig | null> {
  let fetchedTheme: MutablePosterThemeConfig | null = null
  let accessToken = getPosterThemeAccessToken(id)

  if (accessToken) {
    fetchedTheme = await fetchLiveThemeConfig(id, accessToken).catch(
      (error) => {
        const status = getFetchErrorStatus(error)
        if (status === 401 || status === 403) {
          setPosterThemeAccessToken(id, null)
        }
        return null
      },
    )
    accessToken = getPosterThemeAccessToken(id)
  }

  if (!fetchedTheme) {
    fetchedTheme = await fetchLiveThemeConfig(id).catch(async (error) => {
      const status = getFetchErrorStatus(error)
      if (status !== 401 && status !== 403) {
        return null
      }

      const authToken = useAppStore.getState().authToken
      if (!authToken) return null

      const grantedAccessToken = await requestPosterThemeAccess({
        themeId: id,
        authToken,
      }).catch(() => null)
      if (!grantedAccessToken) return null

      setPosterThemeAccessToken(id, grantedAccessToken)
      accessToken = grantedAccessToken
      return fetchLiveThemeConfig(id, grantedAccessToken).catch(() => null)
    })
  }

  if (!fetchedTheme && id === EMBEDDED_DEFAULT_THEME_ID) {
    fetchedTheme = buildEmbeddedDefaultTheme()
  }

  if (!fetchedTheme) return null

  const normalizedHomePreviews = normalizeThemeHomePreviews(
    id,
    fetchedTheme.homePreviews,
  )
  const theme: MutablePosterThemeConfig = {
    ...fetchedTheme,
    id: fetchedTheme.id || id,
    name: fetchedTheme.name || id,
    preview: normalizeThemePreviewAssetValue(id, fetchedTheme.preview),
    ...(normalizedHomePreviews ? { homePreviews: normalizedHomePreviews } : {}),
  }

  theme.preview = theme.preview || svgPoster(theme.name || id)

  if (fetchedTheme.generator) {
    theme.generator = {
      ...fetchedTheme.generator,
      ...(fetchedTheme.generator.originalImage
        ? {
            originalImage: normalizeThemeGeneratorAssetValue(
              id,
              fetchedTheme.generator.originalImage,
            ),
          }
        : {}),
      ...(fetchedTheme.generator.posterMasterImage
        ? {
            posterMasterImage: normalizeThemeGeneratorAssetValue(
              id,
              fetchedTheme.generator.posterMasterImage,
            ),
          }
        : {}),
      ...(fetchedTheme.generator.posterProxyImage
        ? {
            posterProxyImage: normalizeThemeGeneratorAssetValue(
              id,
              fetchedTheme.generator.posterProxyImage,
            ),
          }
        : {}),
      ...(fetchedTheme.generator.logoImage
        ? {
            logoImage: normalizeThemeGeneratorAssetValue(
              id,
              fetchedTheme.generator.logoImage,
            ),
          }
        : {}),
      ...(fetchedTheme.generator.artistLogoImage
        ? {
            artistLogoImage: normalizeThemeGeneratorAssetValue(
              id,
              fetchedTheme.generator.artistLogoImage,
            ),
          }
        : {}),
      ...(fetchedTheme.generator.frameLogoImage
        ? {
            frameLogoImage: normalizeThemeGeneratorAssetValue(
              id,
              fetchedTheme.generator.frameLogoImage,
            ),
          }
        : {}),
    }
  }

  if (fetchedTheme.tiles) {
    theme.tiles = {
      ...fetchedTheme.tiles,
      ...(fetchedTheme.tiles.center
        ? {
            center:
              normalizeThemeTileAssetValue(
                id,
                fetchedTheme.tiles.center,
                'center.png',
              ) || fetchedTheme.tiles.center,
          }
        : {}),
      ...(Array.isArray(fetchedTheme.tiles.set)
        ? {
            set: fetchedTheme.tiles.set.map((tile, index) => {
              return (
                normalizeThemeTileAssetValue(
                  id,
                  tile,
                  `tile-${index + 1}.png`,
                ) || tile
              )
            }),
          }
        : {}),
    }
  }

  if (theme.generator?.colors) {
    const generatedPalette = buildGeneratedThemePalette(
      theme.generator.colors,
      theme.palette,
    )
    theme.palette = generatedPalette
    theme.homePreviews = {
      ...buildGeneratedThemeHomePreviews(generatedPalette.players),
      ...(theme.homePreviews || {}),
    }
    if (!theme.tiles?.center || !theme.tiles?.set?.length) {
      theme.tiles = buildGeneratedThemeTiles(
        theme.generator.colors,
        theme.generator.logoImage,
      )
    }
    if (!theme.puzzlePosterTiles?.length && !fetchedTheme.puzzlePosterBase) {
      theme.puzzlePosterTiles = buildGeneratedThemePuzzlePosterTiles(
        theme.generator.colors,
      )
    }
  }

  if (theme.homePreviews && typeof theme.homePreviews === 'object') {
    try {
      for (const key of HOME_PREVIEW_KEYS) {
        const value = theme.homePreviews[key]
        if (!value) continue
        HOME_PREVIEWS[key] = value
      }
    } catch {
      // ignore
    }
  }

  const puzzlePosterBase = normalizeThemePuzzlePosterBaseAssetValue(
    id,
    fetchedTheme.puzzlePosterBase,
  )
  if (puzzlePosterBase) {
    theme.puzzlePosterBase = puzzlePosterBase
  }

  if (fetchedTheme.puzzlePreviews) {
    const puzzlePreviews = {
      united:
        normalizeThemePreviewAssetValue(
          id,
          fetchedTheme.puzzlePreviews.united,
        ) ||
        fetchedTheme.puzzlePreviews.united ||
        undefined,
      poster:
        normalizeThemePreviewAssetValue(
          id,
          fetchedTheme.puzzlePreviews.poster,
        ) ||
        fetchedTheme.puzzlePreviews.poster ||
        undefined,
      switch:
        normalizeThemePreviewAssetValue(
          id,
          fetchedTheme.puzzlePreviews.switch,
        ) ||
        fetchedTheme.puzzlePreviews.switch ||
        undefined,
    }
    if (
      puzzlePreviews.united ||
      puzzlePreviews.poster ||
      puzzlePreviews.switch
    ) {
      theme.puzzlePreviews = puzzlePreviews
    }
  }

  rememberThemeRuntimeAssets(id, theme)

  return theme
}
